import { memo } from 'react';
import { Package, Download, Trophy, Clock, Star, Activity } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ApogeeActivityWindow, PackEnrichmentResponse, PackRow } from '../../types/apogee';
import { enrichmentAuthorDisplayLine } from '../../utils/apogeeEnrichment';
import { cn } from '../../utils/cn';

interface PackCardProps {
  pack: PackRow;
  /** Optional Modrinth/CurseForge enrichment for author line and search elsewhere */
  enrichment?: PackEnrichmentResponse | null;
  /** Which Apogee activity window to surface on the card (from `apogee_activity.windows`). */
  activityWindow?: ApogeeActivityWindow;
  onClick?: () => void;
  className?: string;
}

const formatNumber = (n: number | null | undefined): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const formatActivityScore = (s: number | undefined): string =>
  s == null || Number.isNaN(s) ? '—' : s.toFixed(1);

export const PackCard = memo(({ pack, enrichment, activityWindow = '30d', onClick, className }: PackCardProps) => {
  const authorLine = enrichmentAuthorDisplayLine(enrichment ?? null);
  const cfFeat = pack.curseforge_featured;
  const act = pack.apogee_activity?.windows?.[activityWindow];
  return (
    <div
      onClick={onClick}
      className={cn(
        'glass-card overflow-hidden group',
        onClick && 'glass-card-interactive',
        className,
      )}
    >
      {/* Thumbnail */}
      <div className="relative w-full h-36 bg-dark-900/50 overflow-hidden">
        {pack.thumbnail_url ? (
          <img
            src={pack.thumbnail_url}
            alt={pack.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              const fallback = (e.target as HTMLImageElement).nextElementSibling;
              if (fallback) (fallback as HTMLElement).style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={cn(
            'absolute inset-0 items-center justify-center bg-dark-900/60',
            pack.thumbnail_url ? 'hidden' : 'flex',
          )}
        >
          <Package className="w-10 h-10 text-glass-400" />
        </div>
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          {cfFeat?.currently_featured && (
            <div
              className="badge bg-amber-500/90 text-on-solid border-transparent backdrop-blur-sm shadow-sm"
              title={cfFeat.since ? `Featured since ${cfFeat.since}` : 'CurseForge featured'}
            >
              <Star className="w-3 h-3 fill-current" />
              CF
            </div>
          )}
          {typeof pack.popularity_rank === 'number' && (
            <div className="badge bg-accent-500/80 text-on-solid border-transparent backdrop-blur-sm shadow-sm">
              #{pack.popularity_rank}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white truncate group-hover:text-accent-300 transition-colors">
            {pack.name}
          </h3>
          <p className="text-xs font-mono text-glass-400 truncate">{pack.slug}</p>
          {authorLine && (
            <p className="text-xs text-glass-500 truncate mt-0.5" title={authorLine}>
              {authorLine}
            </p>
          )}
        </div>

        {pack.summary && (
          <p className="text-xs text-glass-300 line-clamp-2 leading-relaxed">
            {pack.summary}
          </p>
        )}

        {/* Stats row */}
        <div className="flex items-center justify-between pt-2 border-t border-glass-200/10 flex-wrap gap-x-3 gap-y-1 text-xs text-glass-400 tabular-nums">
          <div className="flex items-center gap-1">
            <Download className="w-3 h-3" />
            <span className="font-medium text-glass-300">{formatNumber(pack.download_count)}</span>
          </div>
          {act && (
            <div className="flex items-center gap-1 text-violet-300" title={`Apogee activity · ${activityWindow}`}>
              <Activity className="w-3 h-3" />
              <span>{formatActivityScore(act.score)}</span>
              <span className="text-glass-500">#{act.rank}</span>
            </div>
          )}
          {typeof pack.popularity_rank === 'number' && (
            <div className="flex items-center gap-1">
              <Trophy className="w-3 h-3" />
              <span>#{pack.popularity_rank}</span>
            </div>
          )}
          {pack.last_updated ? (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatDistanceToNow(new Date(pack.last_updated), { addSuffix: true })}</span>
            </div>
          ) : pack.snapshot_observed_at ? (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatDistanceToNow(new Date(pack.snapshot_observed_at), { addSuffix: true })}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

PackCard.displayName = 'PackCard';
